import React from "react";
import Section from "./Section";

export default function HTML_HSL() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🎨 HTML HSL Colors</h1>

      {/* 🔹 Highlighted Intro Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "2rem",
        }}
      >
        <p className="tutorial-intro">
          HSL stands for <strong>Hue</strong>, <strong>Saturation</strong>, and{" "}
          <strong>Lightness</strong>. It’s another way to define colors in CSS
          and HTML — easier to understand and modify than RGB or HEX.
        </p>
      </div>

      {/* 🔹 Explanation */}
      <p>
        HSL color values are written as:{" "}
        <code>hsl(hue, saturation%, lightness%)</code>. - <b>Hue</b> is the
        color angle on the color wheel (0–360). - <b>Saturation</b> is the
        intensity of the color (0% = gray, 100% = full color). -{" "}
        <b>Lightness</b> controls brightness (0% = black, 100% = white).
      </p>

      {/* Section 1️⃣ — Basic HSL Colors */}
      <Section
        title="1️⃣ Basic HSL Examples"
        html={`<div style="background-color: hsl(0, 100%, 50%); color: white; padding: 10px;">hsl(0, 100%, 50%) — Red</div>
<div style="background-color: hsl(120, 100%, 50%); padding: 10px;">hsl(120, 100%, 50%) — Green</div>
<div style="background-color: hsl(240, 100%, 50%); color: white; padding: 10px;">hsl(240, 100%, 50%) — Blue</div>`}
        description="HSL makes it easy to control colors using hue degrees — 0° (red), 120° (green), and 240° (blue)."
      />

      {/* Section 2️⃣ — Adjusting Lightness */}
      <Section
        title="2️⃣ Adjusting Lightness"
        html={`<div style="background-color: hsl(200, 100%, 25%); color: white; padding: 10px;">hsl(200, 100%, 25%) — Dark Blue</div>
<div style="background-color: hsl(200, 100%, 50%); color: white; padding: 10px;">hsl(200, 100%, 50%) — Normal Blue</div>
<div style="background-color: hsl(200, 100%, 75%); padding: 10px;">hsl(200, 100%, 75%) — Light Blue</div>`}
        description="Changing the lightness value adjusts how bright or dark a color appears."
      />

      {/* Section 3️⃣ — Adjusting Saturation */}
      <Section
        title="3️⃣ Adjusting Saturation"
        html={`<div style="background-color: hsl(0, 100%, 50%); color: white; padding: 10px;">hsl(0, 100%, 50%) — Full Saturation</div>
<div style="background-color: hsl(0, 50%, 50%); color: white; padding: 10px;">hsl(0, 50%, 50%) — Medium Saturation</div>
<div style="background-color: hsl(0, 0%, 50%); color: white; padding: 10px;">hsl(0, 0%, 50%) — No Saturation (Gray)</div>`}
        description="Reducing saturation removes color intensity — at 0%, the result is grayscale."
      />

      {/* Section 4️⃣ — Using HSLA (With Alpha) */}
      <Section
        title="4️⃣ HSLA (Transparency Support)"
        html={`<div style="background-color: hsla(0, 100%, 50%, 0.5); color: white; padding: 10px;">hsla(0, 100%, 50%, 0.5) — Transparent Red</div>
<div style="background-color: hsla(120, 100%, 50%, 0.5); padding: 10px;">hsla(120, 100%, 50%, 0.5) — Transparent Green</div>
<div style="background-color: hsla(240, 100%, 50%, 0.5); color: white; padding: 10px;">hsla(240, 100%, 50%, 0.5) — Transparent Blue</div>`}
        description="HSLA adds an alpha value for transparency (0 = fully transparent, 1 = fully opaque)."
      />

      {/* Section 5️⃣ — HSL Gradients */}
      <Section
        title="5️⃣ HSL Gradient Example"
        html={`<div style="background: linear-gradient(to right, hsl(0, 100%, 50%), hsl(120, 100%, 50%), hsl(240, 100%, 50%)); height: 80px; border-radius: 10px;"></div>`}
        description="You can combine HSL colors in gradients for beautiful color transitions."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
          marginBottom: "2rem",
        }}
      >
        <h2>📘 Summary — HSL Color Essentials</h2>
        <ul className="tutorial-list-points">
          <li>
            HSL stands for <b>Hue</b>, <b>Saturation</b>, and <b>Lightness</b>.
          </li>
          <li>Hue values: 0° (red), 120° (green), 240° (blue).</li>
          <li>Saturation controls intensity; Lightness controls brightness.</li>
          <li>HSLA adds an alpha channel for transparency.</li>
          <li>
            HSL is great for easily adjusting color tones and brightness in CSS.
          </li>
        </ul>
      </div>
    </div>
  );
}
